import { CreateListRequest } from '@app/models/requests';
import { ListInterface } from '@app/models/util';
import { getCustomers } from '@app/services/customers';
import { createList, getLists, getPaginatedLists } from '@app/services/list';
import { createListValidation } from '@app/utils/validations';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

export const useListPage = () => {
  const [addListOpen, setAddListOpen] = useState<boolean>(false);
  const [addCustomersToList, setAddCustomersToList] = useState<boolean>(false);
  const [listQuery, setListQuery] = useState({ page: 1, limit: 10 });
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const createListForm = useForm({ resolver: createListValidation });
  // Lists passed to the delete dialog: the checkbox selection or a single row
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [amountOfSelectedCustomers, setAmountOfSelectedCustomers] =
    useState<number>(0);

  const { data: allListsData } = useQuery<{
    lists: ListInterface[];
    count: number;
  }>({
    queryKey: ['all-lists'],
    queryFn: () => {
      return getLists()
        .then((res) => res?.data)
        .catch((err) => err);
    },
  });

  const {
    data: listsData,
    isLoading,
    refetch,
  } = useQuery<{ lists: ListInterface[]; count: number }>({
    queryKey: ['lists-paginated', listQuery.page, listQuery.limit, sortBy, sortOrder],
    queryFn: () => {
      const limit = listQuery?.limit ?? 10;
      const page = listQuery?.page ?? 1;
      const skip = (page - 1) * limit;

      return getPaginatedLists({
        pagination: { limit, page, skip },
        sortBy,
        sortOrder,
      })
        .then((res) => res?.data)
        .catch((err) => err);
    },
  });

  const { data: customers } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => getCustomers(),
  });

  const handleAddList = () => {
    setAddListOpen(!addListOpen);
    if (!addListOpen) createListForm.reset();
  };

  const handleAddCustomersToList = () => {
    setAddCustomersToList(!addCustomersToList);
  };

  const handleDeleteListModal = () => {
    setDeleteIds(selectedLists);
  };

  const handleDeleteList = (id: string) => {
    setDeleteIds([id]);
  };

  const closeDeleteDialog = () => {
    setDeleteIds(null);
  };

  const isDeletingSelection = deleteIds === selectedLists;
  const deletingNames = (deleteIds ?? []).map((id) => listsData?.lists.find((list) => list.id === id)?.name ?? `#${id}`);
  const deletingCustomersCount = isDeletingSelection
    ? amountOfSelectedCustomers
    : (listsData?.lists.find((list) => list.id === deleteIds?.[0])?.customers?.length ?? 0);

  const countCustomerPerList = (id: string | undefined) => {
    if (!id) return;
    const list = listsData?.lists.find((item) => item.id === id);
    const customerCount = list?.customers?.length ?? 0;
    const multiplier = selectedLists.includes(id) ? -1 : 1;
    setAmountOfSelectedCustomers(prev => prev + (customerCount * multiplier));
  };

  const handleSelectedLists = (id: string | undefined) => {
    countCustomerPerList(id);

    if (id) {
      if (id === 'all') {
        if (selectedLists.length === listsData?.lists.length) {
          setSelectedLists([]);
          setAmountOfSelectedCustomers(0);
        } else {
          setSelectedLists(listsData?.lists.map((item) => item.id) ?? []);
          setAmountOfSelectedCustomers((listsData?.lists ?? []).reduce((acc, item) => {
            return acc + (item.customers?.length ?? 0);
          }, 0));
        }
        return;
      }

      if (selectedLists.includes(id)) {
        setSelectedLists((prev) => prev.filter((item) => item !== id));
      } else {
        setSelectedLists((prev) => [...prev, id]);
      }
    }
  };

  const { mutateAsync: addList, isPending: isPendingList } = useMutation({
    mutationKey: ['add-list'],
    mutationFn: (data: CreateListRequest) => createList(data),
    onSuccess: () => {
      refetch();
      handleAddList();
      createListForm.reset();
    },
    onError: (err) => {
      toast.error('Error creating list');
      console.error(err);
    },
  });

  const submitList = async (values: FieldValues) => {
    await addList(values as CreateListRequest);
  };

  const handleNextPage = () => {
    setListQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  };

  const handlePrevPage = () => {
    setListQuery((prev) => ({
      ...prev,
      page: prev.page === 0 ? 0 : prev.page - 1,
    }));
  };

  const handleLimit = (value: any) => {
    setListQuery((prev) => ({ ...prev, limit: value }));
  };

  const handleSortingTable = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleListsDeleted = () => {
    setSelectedLists([]);
    setAmountOfSelectedCustomers(0);
    setListQuery((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  useEffect(() => {
    if (listsData) {
      refetch();
    }
  }, [listQuery.limit, listQuery.page, sortBy, sortOrder]);

  return {
    addListOpen,
    handleAddList,
    createListForm,
    submitList,
    isPendingList,
    listsData,
    isLoading,
    customers,
    handleNextPage,
    handlePrevPage,
    handleLimit,
    listQuery,
    refetch,
    handleAddCustomersToList,
    addCustomersToList,
    handleSelectedLists,
    selectedLists,
    allListsData,
    handleDeleteListModal,
    handleDeleteList,
    closeDeleteDialog,
    deleteIds,
    deletingNames,
    deletingCustomersCount,
    handleListsDeleted,
    sortBy,
    sortOrder,
    handleSortingTable,
  };
};
